import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { saveArtistVotesMutationFn } from '../api/artistVotes';
import { queryKeys } from '../lib/query/queryKeys';
import { voteKey } from '../utils/lineupFormat';

/**
 * Toggle the viewer's pick for one artist on one day, then refresh their picks and (when a group is in view) the
 * group's overlap counts. Shared by the group Lineup page and the Sets page.
 */
export default function useArtistVoteToggle({ eventId, viewerId, groupId = null, selectedKeys }) {
  const queryClient = useQueryClient();
  return useCallback(
    async (artistId, performanceDay) => {
      if (!viewerId || !eventId) return;
      const key = voteKey(artistId, performanceDay);
      const next = new Set(selectedKeys);
      if (next.has(key)) next.delete(key);
      else next.add(key);

      const selections = [...next].map((k) => {
        const [id, day] = k.split('|');
        return { artistId: id, performanceDay: day || null };
      });

      await saveArtistVotesMutationFn({ eventId, userId: viewerId, selections });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.events.myArtistVotes(eventId, viewerId),
        }),
        groupId
          ? queryClient.invalidateQueries({
              queryKey: queryKeys.chats.memberVoters(groupId, eventId),
            })
          : Promise.resolve(),
      ]);
    },
    [viewerId, eventId, groupId, selectedKeys, queryClient],
  );
}
